import FacebookIcon from '@assets/icons/socials/facebook.svg?react';
import { TBlogPostDetails } from '@blog/types/blog';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import ShareIcon from '@mui/icons-material/Share';
import XIcon from '@mui/icons-material/X';
import { Avatar, AvatarGroup, Collapse, Paper, Typography } from '@mui/material';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

type TBlogHeroProps = Omit<TBlogPostDetails, 'content' | 'categories' | 'related_posts'> & {
    categories: string[];
    className?: string;
};

const ShareButton = ({ title, className }: { title: string; className?: string }) => {
    const [shareExpanded, setShareExpanded] = useState(false);

    const handleShareButton = useCallback(() => {
        setShareExpanded((prev) => !prev);
    }, []);

    const { facebookLink, linkedinLink, xLink } = useMemo(() => {
        const location = window.location.href;

        const facebookLink = `https://www.facebook.com/sharer.php?u=${location}&title=${title}`;
        const xLink = `https://x.com/share?url=${location}`;
        const linkedinLink = `https://www.linkedin.com/shareArticle?mini=true&url=${location}&title=${title}`;

        return { facebookLink, linkedinLink, xLink };
    }, [title]);

    return (
        <Paper
            className={clsx(className, 'flex items-center rounded-full w-fit h-fit p-2 flex-row-reverse lg:flex-row')}
        >
            <Collapse in={shareExpanded} orientation="horizontal">
                <div className="flex items-center gap-4 flex-row-reverse lg:flex-row">
                    <p className="tracking-wide font-inter mr-4 lg:mr-0 ml-0 lg:ml-4">
                        <FormattedMessage id="common.shareTo" defaultMessage="Share to" />
                    </p>
                    <a href={facebookLink} target="_blank">
                        <FacebookIcon />
                    </a>
                    <a href={xLink} target="_blank">
                        <XIcon />
                    </a>
                    <a href={linkedinLink} target="_blank" className="ml-4 lg:ml-0 mr-0 lg:mr-4">
                        <LinkedInIcon />
                    </a>
                </div>
            </Collapse>

            <button
                className="flex justify-center items-center bg-spanish-blue rounded-full p-1"
                onClick={handleShareButton}
                aria-pressed={shareExpanded}
            >
                <ShareIcon className="fill-white" />
            </button>
        </Paper>
    );
};

export const BlogHero = ({ author, title, date, categories, className = '' }: TBlogHeroProps) => {
    const authorNames = author.map((auth) => auth.name).join(', ');

    return (
        <div className={className}>
            <div className="flex justify-between group">
                <Typography variant="h1" className="text-3xl sm:text-5xl font-museo font-semibold lg:max-w-[60%]">
                    {title}
                </Typography>
                <ShareButton title={title} className="hidden lg:flex" />
            </div>

            <div className="flex items-center gap-2 my-4">
                <AvatarGroup>
                    {author.map((auth) => (
                        <Avatar src={auth.avatar_urls[48]} alt={auth.name} key={auth.id} />
                    ))}
                </AvatarGroup>
                <span className="font-inter text-xl">
                    <span>{authorNames}</span> • <span>{format(date, 'MMM d, yyyy')}</span>
                </span>
            </div>
            <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                    <div
                        className="text-white font-medium bg-gradient-to-r from-primary-700 to-primary-600 w-fit px-3 py-1 capitalize rounded-full"
                        key={cat}
                    >
                        {cat}
                    </div>
                ))}
            </div>
            <ShareButton title={title} className="flex lg:hidden my-6" />
        </div>
    );
};
